import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PopupNames } from 'core';
import {
  useAllFakturowniaInvoices,
  usePopupStateOperations,
  IFakturowniaInvoiceListRow,
} from 'core/hooks';
import { KpiCard, KpiGrid } from 'molecules';

import { InvoicesTable } from '../../FakturowniaInvoices/InvoicesTable';
import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../StudentDetail.styled';
import { formatMoneyCurrency } from './format';
import { IAgreement } from './types';

interface IInvoicesBlockProps {
  agreement: IAgreement;
  /** Fired after a `LinkInvoicePaymentPopup` mutation succeeds so the parent
   *  can refetch the student detail (which carries `agreement.paidAmount`). */
  onChanged?: () => void;
}

/**
 * Invoices for a single agreement — Phase 13 canonical filter: server
 * `agreementId` does the heavy lifting (instead of student-by-email).
 * Three KPI cards above the table mirror the per-agreement summary so the
 * financier sees «what we billed / got / are owed» without reading rows.
 */
export const InvoicesBlock = memo(
  ({ agreement, onChanged }: IInvoicesBlockProps) => {
    const { t } = useTranslation();
    const {
      operations: { showPopup },
    } = usePopupStateOperations();
    const { data, fetching } = useAllFakturowniaInvoices({
      agreementId: agreement.id,
      direction: 'issued',
    });

    const kpis = useMemo(() => {
      let billed = 0;
      let paid = 0;
      let outstanding = 0;
      let currency = agreement.currency || '';
      data.forEach((row) => {
        billed += row.priceGross || 0;
        paid += row.paid || 0;
        outstanding +=
          row.status?.toUpperCase() === 'PAID' ? 0 : row.outstanding || 0;
        currency = currency || row.currency || '';
      });
      return { billed, paid, outstanding, currency };
    }, [data, agreement.currency]);

    const onLinkRow = useCallback(
      (row: IFakturowniaInvoiceListRow) => {
        showPopup(PopupNames.linkInvoicePayment, {
          invoiceId: row.id,
          mode: 'link',
          buyerEmail: row.buyerEmail,
          onLinked: onChanged,
        });
      },
      [showPopup, onChanged],
    );

    return (
      <L.Subblock>
        <L.SubblockHeader>
          {t('pages.studentDetail.invoices.title')}
          <L.SubblockHeaderCount>· {data.length}</L.SubblockHeaderCount>
        </L.SubblockHeader>

        <S.DimWhenFetching fetching={fetching}>
          <KpiGrid columns={3}>
            <KpiCard
              label={t('pages.studentDetail.invoices.billed')}
              value={formatMoneyCurrency(kpis.billed, kpis.currency)}
            />
            <KpiCard
              label={t('pages.studentDetail.invoices.paid')}
              value={formatMoneyCurrency(kpis.paid, kpis.currency)}
              accent="#16a34a"
            />
            <KpiCard
              label={t('pages.studentDetail.invoices.outstanding')}
              value={formatMoneyCurrency(kpis.outstanding, kpis.currency)}
              accent={kpis.outstanding > 0 ? '#d97706' : undefined}
            />
          </KpiGrid>

          {data.length === 0 ? (
            <S.EmptyState>
              {t('pages.studentDetail.invoices.emptyAgreement')}
            </S.EmptyState>
          ) : (
            <InvoicesTable rows={data} onLinkRow={onLinkRow} />
          )}
        </S.DimWhenFetching>
      </L.Subblock>
    );
  },
);
